
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApplicationResponse } from "@src/common/domain/common.model";
import api from "@src/common/services/api.service";
import AppConstants from "@src/utils/app.constants";
import { Report, ReportsStore } from "@src/views/reports/domain/reports.model";
import {string} from "yup";

interface ReportFilters {
    customerName?: string | null;
    drumNumber?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    page?: number | null;
    size?: number | null;
    numberOfCores?: number | null;
}

const buildFilterParams = (filters: ReportFilters , numberOfCores: number): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("numberOfCores", numberOfCores.toString());

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
            params.append(key, value === null ? "" : value.toString());
        }
    });

    return params;
};

export const fetchThreeCoresReports = createAsyncThunk<any[], {filters: ReportFilters | undefined, headers?: Record<string, string>}>(
    "reports/fetchThreeCoresReports",
    async ({filters= {}, headers =  {}}) => {
        const params = buildFilterParams(filters, 3);
        const url = `${AppConstants.api}/drums`;
        const response = await api.get<Report[]>(url, { params, headers: headers });

        return response.data || [];
    }
);

export const fetchOneCoresReports = createAsyncThunk<any[],  {filters:ReportFilters | undefined , headers?: Record<string, string>}>(
    "reports/fetchOneCoresReports",
    async ({filters = {}, headers = {}}) => {
        console.log("Headers......",headers)
        const params = buildFilterParams(filters, 1);
        const url = `${AppConstants.api}/drums`;
        const existingHeaders = api.defaults.headers.common || {};
        console.log("Existing headers...",existingHeaders)
        const response = await api.get<Report[]>(url, { params, headers: headers });
        return response.data || [];
    }
);


export const fetchDrumSearch = createAsyncThunk<any, {customerName?: string, numberOfCores?: string} | undefined>(
    "drum/fetchDrumSearch",
    async ({customerName, numberOfCores} = {numberOfCores: String(1)}) => {
        const url = customerName ? `${AppConstants.api}/drums/numbers?customerNames=${customerName}&numberOfCores=${numberOfCores}` : `${AppConstants.api}/drums/numbers?numberOfCores=${numberOfCores}`;
        const response = await api.get<any>(url);
        console.log("Fetched Drum Search Data: ", response.data);
        return response.data;
    }
);

const initialState: ReportsStore = {
    minimal: { status: "idle", error: null, data: [] },
    search: { status: "idle", error: null, data: [] },
    oneCoreReports: { status: "idle", error: null, data: [] },
};

const reportsSlice = createSlice({
    name: "reports",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchThreeCoresReports.pending, (state) => {
                state.minimal.status = "loading";
                state.minimal.data = [];
                state.minimal.error = null;
            })
            .addCase(fetchThreeCoresReports.fulfilled, (state, action) => {
                state.minimal.status = "idle";
                state.minimal.data = action.payload ?? [];
                state.minimal.error = null;
            })
            .addCase(fetchThreeCoresReports.rejected, (state, action) => {
                state.minimal.status = "failed";
                state.minimal.data = [];
                state.minimal.error = action.error.message ?? "Something went wrong";
            })

            .addCase(fetchOneCoresReports.pending, (state) => {
                state.oneCoreReports.status = "loading";
                state.oneCoreReports.data = [];
                state.oneCoreReports.error = null;
            })
            .addCase(fetchOneCoresReports.fulfilled, (state, action) => {
                state.oneCoreReports.status = "idle";
                state.oneCoreReports.data = action.payload ?? [];
                state.oneCoreReports.error = null;
            })
            .addCase(fetchOneCoresReports.rejected, (state, action) => {
                state.oneCoreReports.status = "failed";
                state.oneCoreReports.data = [];
                state.oneCoreReports.error = action.error.message ?? "Something went wrong";
            })

            .addCase(fetchDrumSearch.pending, (state) => {
                state.search.status = "loading";
                state.search.data = [];
                state.search.error = null;
            })
            .addCase(fetchDrumSearch.fulfilled, (state, action) => {
                state.search.status = "idle";
                state.search.data = action.payload ?? [];
                state.search.error = null;
            })
            .addCase(fetchDrumSearch.rejected, (state, action) => {
                state.search.status = "failed";
                state.search.data = [];
                state.search.error = action.error.message ?? "Something went wrong";
            });
    },
});

export default reportsSlice.reducer;
